/**
 * PmMorphIcon 项目名 → Lucide 描边数据映射（唯一登记处）。
 * 业务代码只认「项目名」；禁止 import Lucide 原名（Menu、Trash2…）或把 Lucide 名当 prop。
 */
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Box,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  Edit,
  Eye,
  EyeOff,
  FileText,
  Folder,
  GitCompare,
  LayoutGrid,
  Link2,
  LoaderCircle,
  Menu,
  Minus,
  PenLine,
  Plus,
  Search,
  Settings,
  Trash2,
  Unlink,
  Upload,
  X,
} from 'lucide';

/**
 * @typedef {Object} PmMorphIconMapEntry
 * @property {string} name 项目内使用的图标名（PmIcon 风格）
 * @property {string} lucide 来源 Lucide 导出名（仅维护者对照，勿写入业务代码）
 * @property {import('lucide').IconNode} data
 * @property {string} [pmIcon] 对应 PmIcon symbol 名（有则优先用 PmIcon）
 * @property {string} note 用途说明
 */

/** @type {PmMorphIconMapEntry[]} */
export const PM_MORPH_ICON_MAP_ENTRIES = [
  { name: 'Close_24', lucide: 'X', data: X, pmIcon: 'Close_24', note: '关闭' },
  { name: 'Nav_menu', lucide: 'Menu', data: Menu, note: '侧栏/抽屉菜单（Pm 无等价 symbol）' },
  { name: 'Check', lucide: 'Check', data: Check, pmIcon: 'Check', note: '勾选' },
  { name: 'Add', lucide: 'Plus', data: Plus, pmIcon: 'Add', note: '新增' },
  { name: 'Morph_minus', lucide: 'Minus', data: Minus, note: '减/收起（补充）' },
  { name: 'Search', lucide: 'Search', data: Search, pmIcon: 'Search', note: '搜索' },
  { name: 'Edit', lucide: 'Edit', data: Edit, pmIcon: 'Edit', note: '编辑' },
  { name: 'Del', lucide: 'Trash2', data: Trash2, pmIcon: 'Del', note: '删除' },
  { name: 'Download', lucide: 'Download', data: Download, pmIcon: 'Download', note: '下载' },
  { name: 'Upload1', lucide: 'Upload', data: Upload, pmIcon: 'Upload1', note: '上传' },
  { name: 'Set', lucide: 'Settings', data: Settings, pmIcon: 'Set', note: '设置' },
  { name: 'Link2', lucide: 'Link2', data: Link2, pmIcon: 'Link2', note: '关联' },
  { name: 'unlink', lucide: 'Unlink', data: Unlink, pmIcon: 'unlink', note: '取消关联' },
  { name: 'Arrow_down', lucide: 'ArrowDown', data: ArrowDown, pmIcon: 'Arrow_down', note: '箭头向下' },
  { name: 'Arrow_left', lucide: 'ArrowLeft', data: ArrowLeft, pmIcon: 'Arrow_left', note: '箭头向左' },
  { name: 'Arrow_right', lucide: 'ArrowRight', data: ArrowRight, pmIcon: 'Arrow_right', note: '箭头向右' },
  { name: 'Arrow_Up', lucide: 'ArrowUp', data: ArrowUp, pmIcon: 'Arrow_Up', note: '箭头向上' },
  { name: 'Down', lucide: 'ChevronDown', data: ChevronDown, pmIcon: 'Down', note: 'Chevron 向下' },
  { name: 'Chevron_left', lucide: 'ChevronLeft', data: ChevronLeft, note: 'Chevron 向左（补充）' },
  { name: 'Chevron_right', lucide: 'ChevronRight', data: ChevronRight, note: 'Chevron 向右（补充）' },
  { name: 'Chevron_up', lucide: 'ChevronUp', data: ChevronUp, note: 'Chevron 向上（补充）' },
  { name: 'Folder', lucide: 'Folder', data: Folder, pmIcon: 'Folder', note: '文件夹' },
  { name: 'File', lucide: 'FileText', data: FileText, pmIcon: 'File', note: '文件' },
  { name: 'Eye_24', lucide: 'Eye', data: Eye, pmIcon: 'Eye_24', note: '查看' },
  { name: 'Eye_close_24', lucide: 'EyeOff', data: EyeOff, pmIcon: 'Eye_close_24', note: '隐藏' },
  { name: 'Refresh_24', lucide: 'LoaderCircle', data: LoaderCircle, pmIcon: 'Refresh_24', note: '加载/刷新' },
  { name: 'Morph_grid', lucide: 'LayoutGrid', data: LayoutGrid, note: '全部/网格筛选（补充）' },
  { name: 'Morph_compare', lucide: 'GitCompare', data: GitCompare, note: '图纸/模型对比（补充）' },
  { name: 'Morph_box', lucide: 'Box', data: Box, note: '模型对比（补充）' },
  { name: 'Morph_pen', lucide: 'PenLine', data: PenLine, note: '手动创建（补充）' },
];

/** @type {Record<string, import('lucide').IconNode>} */
export const PM_MORPH_ICON_MAP = Object.fromEntries(
  PM_MORPH_ICON_MAP_ENTRIES.map((entry) => [entry.name, entry.data]),
);

export const pmMorphIconNames = PM_MORPH_ICON_MAP_ENTRIES.map((entry) => entry.name);

export function hasPmMorphIcon(name) {
  return Object.prototype.hasOwnProperty.call(PM_MORPH_ICON_MAP, name);
}

export function getPmMorphIconData(name) {
  return PM_MORPH_ICON_MAP[name];
}

/**
 * 解析 PmMorphIcon 的 icon / from / to / name：项目名字符串 → IconNode。
 * @param {string | import('lucide').IconNode | undefined} input
 */
export function resolvePmMorphIconInput(input) {
  if (input == null) return input;
  if (typeof input === 'string' && hasPmMorphIcon(input)) {
    return getPmMorphIconData(input);
  }
  return input;
}

/** 形变对：项目名 ↔ 项目名（供文档与演示） */
export const PM_MORPH_ICON_PAIRS = [
  { id: 'nav-toggle', off: 'Nav_menu', on: 'Close_24', label: '菜单 ↔ 关闭' },
  { id: 'add-check', off: 'Add', on: 'Check', label: '新增 ↔ 勾选' },
  { id: 'eye-toggle', off: 'Eye_24', on: 'Eye_close_24', label: '显示 ↔ 隐藏' },
  { id: 'link-toggle', off: 'Link2', on: 'unlink', label: '关联 ↔ 取消关联' },
  { id: 'chevron-expand', off: 'Chevron_right', on: 'Down', label: '收起 ↔ 展开' },
  { id: 'arrow-turn', off: 'Arrow_right', on: 'Arrow_down', label: '向右 ↔ 向下' },
  { id: 'load-done', off: 'Refresh_24', on: 'Check', label: '加载 ↔ 完成' },
];
